import React, { useEffect, useState } from 'react';
import { Header } from '../components/Layout/Header';
import { usersApi } from '../services/api';
import { User } from '../types';
import { Mail, Shield, Plus, Pencil, Trash2, X } from 'lucide-react';
import { Badge } from '../components/common/Badge';
import { Button } from '../components/common/Button';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import axios from 'axios';

interface UserFormData {
  name: string;
  email: string;
  password: string;
  role: 'admin' | 'member' | 'finance';
}

function UserModal({
  user,
  onClose,
  onSave,
}: {
  user?: User | null;
  onClose: () => void;
  onSave: (data: UserFormData) => Promise<void>;
}) {
  const [form, setForm] = useState<UserFormData>({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role === 'super_admin' ? 'admin' : user?.role || 'member',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(form);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={18} />
        </button>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {user ? 'Editar Membro' : 'Adicionar Membro'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              placeholder="Nome completo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              disabled={!!user}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm disabled:bg-gray-50 disabled:text-gray-400"
              placeholder="email@exemplo.com"
            />
          </div>
          {!user && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha *</label>
              <input
                type="password"
                required
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                placeholder="Mínimo 6 caracteres"
                minLength={6}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Função</label>
            <select
              value={form.role}
              onChange={e => setForm(p => ({ ...p, role: e.target.value as 'admin' | 'member' | 'finance' }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="member">Membro</option>
              <option value="finance">Financeiro</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors"
            >
              {loading ? 'Salvando…' : user ? 'Salvar' : 'Adicionar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function TeamPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalUser, setModalUser] = useState<User | null | undefined>(undefined);
  const { user: currentUser } = useAuthStore();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  const load = () => {
    setLoading(true);
    usersApi.getAll()
      .then(res => setUsers(res.data))
      .catch(() => toast.error('Falha ao carregar equipe'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (data: UserFormData) => {
    try {
      if (modalUser) {
        await usersApi.update(modalUser.id, { name: data.name.trim(), role: data.role });
        toast.success('Membro atualizado!');
      } else {
        await usersApi.create({
          ...data,
          name: data.name.trim(),
          email: data.email.trim().toLowerCase(),
        });
        toast.success('Membro adicionado!');
      }
      setModalUser(undefined);
      load();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          toast.error('Este e-mail já está cadastrado.');
          return;
        }
        if (error.response?.status === 403) {
          toast.error('Você não tem permissão para gerenciar membros.');
          return;
        }
        const message = error.response?.data?.message;
        if (typeof message === 'string') {
          toast.error(message);
          return;
        }
      }
      toast.error(modalUser ? 'Falha ao atualizar membro.' : 'Falha ao adicionar membro.');
    }
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Remover ${user.name}? O e-mail poderá ser cadastrado novamente.`)) return;
    try {
      await usersApi.delete(user.id);
      toast.success('Membro removido');
      load();
    } catch {
      toast.error('Falha ao remover membro');
    }
  };

  return (
    <div>
      <Header title="Equipe" />
      <div className="p-6">
        {isAdmin && (
          <div className="flex justify-end mb-4">
            <Button icon={<Plus size={16} />} onClick={() => setModalUser(null)}>
              Adicionar Membro
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {users.map(user => (
              <div key={user.id} className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{user.name}</h3>
                      {(user.role === 'admin' || user.role === 'super_admin' || user.role === 'finance') && (
                        <Badge variant="info">
                          <Shield size={10} className="mr-1" />
                          {user.role === 'super_admin' ? 'Proprietário' : user.role === 'finance' ? 'Financeiro' : 'Admin'}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <Mail size={12} className="text-gray-400" />
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Entrou em {format(new Date(user.created_at), 'MMM yyyy', { locale: ptBR })}
                    </p>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setModalUser(user)}
                        className="p-1.5 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                        title="Editar"
                      >
                        <Pencil size={14} />
                      </button>
                      {user.id !== currentUser?.id && (
                        <button
                          type="button"
                          onClick={() => handleDelete(user)}
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Remover"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalUser !== undefined && (
        <UserModal
          user={modalUser}
          onClose={() => setModalUser(undefined)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
